import type { FeatureGridBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

const ICON_MAP: Record<string, string> = {
  home: '🏠', 'home-search': '🏠', house: '🏠', search: '🔍',
  shield: '🛡️', 'shield-check': '🛡️', lock: '🔒', star: '⭐',
  chart: '📈', 'chart-line': '📈', graph: '📈', data: '📊',
  heart: '❤️', trophy: '🏆', award: '🏅', medal: '🏅',
  users: '👥', team: '👥', user: '👤', handshake: '🤝',
  headset: '🎧', phone: '📞', mail: '✉️', email: '✉️',
  key: '🔑', map: '🗺️', pin: '📍', location: '📍',
  money: '💲', dollar: '💲', tag: '🏷️', clock: '⏰',
  check: '✓', 'check-circle': '✓', sparkles: '✨', building: '🏢',
};

/**
 * Map a common icon hint (name or emoji) to a glyph. Dependency-free (this package
 * is framework-agnostic — no icon library), so an unknown name falls back to a dot.
 * Authors can also put an emoji directly in the `icon` field — any non-ASCII string
 * is used verbatim.
 */
function iconGlyph(icon?: string): string {
  if (!icon) return '◆';
  const raw = icon.trim();
  if (!raw) return '◆';
  // A non-ASCII string is almost certainly an emoji the author typed → use as-is.
  for (const ch of raw) {
    if (ch.codePointAt(0)! > 127) return raw;
  }
  return ICON_MAP[raw.toLowerCase()] ?? '◆';
}

/**
 * Pure feature/values grid — icon + heading + body tiles (why-us, values, services).
 * Static content; renders nothing when empty.
 */
export function FeatureGridBlock(props: FeatureGridBlockProps) {
  const { heading, subheading, features = [] } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (!Array.isArray(features) || features.length === 0) return null;

  const grid = features.length <= 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-6xl">
        {heading ? (
          <h2
            className="text-center text-2xl font-semibold"
            style={{ fontFamily: theme.headingFont }}
          >
            {heading}
          </h2>
        ) : null}
        {subheading ? (
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm opacity-70">{subheading}</p>
        ) : null}
        <div className={`mt-8 grid grid-cols-1 gap-6 ${grid}`}>
          {features.map((f, i) => (
            <div key={i} className="rounded-lg border border-black/10 p-6">
              <div
                className="mb-3 flex h-11 w-11 items-center justify-center rounded-full text-lg"
                style={{ backgroundColor: theme.accent, color: theme.secondary }}
                aria-hidden="true"
              >
                {iconGlyph(f.icon)}
              </div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: theme.headingFont }}>
                {f.heading}
              </h3>
              <p className="mt-2 text-sm leading-relaxed opacity-70">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
